/** @jsxImportSource @emotion/react */

import React, { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import {
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
} from '@material-ui/core'
import CachedIcon from '@material-ui/icons/Cached'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import LaunchIcon from '@material-ui/icons/Launch'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import { toast } from 'react-toastify'
import moment from 'moment'
import { GET_USER_TASKS } from '../../graphql/task.graphql'
import { colors } from '../../style'

const dayStart = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

const taskTime = (task) => {
  const value = task?.start_date || task?.due_date
  const timestamp = Number(value)
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : null
}

const taskUrl = (id) => `https://app.clickup.com/t/${String(id).replace(/^#/, '')}`

const ClickupTasksWidget = ({ onSelectTask, onStartTask, isLoading }) => {
  const [open, setOpen] = useState(true)
  const clickupConnected = Boolean(localStorage.getItem('clickup'))
  const { data, loading, error, refetch } = useQuery(GET_USER_TASKS, {
    skip: !clickupConnected,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  const tasks = useMemo(() => {
    const now = Date.now()
    const start = dayStart(new Date())
    const end = start + 24 * 60 * 60 * 1000
    const contextStart = now - 2 * 60 * 60 * 1000
    const contextEnd = now + 2 * 60 * 60 * 1000

    return (data?.getUserTasks || [])
      .filter((task) => {
        const time = taskTime(task)
        return !time || (time >= start && time < end) || (time >= contextStart && time <= contextEnd)
      })
      .map((task) => {
        const time = taskTime(task)
        const estimate = task.estimate || {}
        const projectRequired = Boolean(estimate.company?.require_projects)
        const ready = Boolean(
          task.name &&
            estimate.company &&
            estimate.category &&
            (!projectRequired || estimate.project)
        )
        const overdue = time && time < now && !['closed', 'complete', 'completed'].includes(task.status?.status?.toLowerCase())
        const priority = overdue ? 0 : time && time <= contextEnd ? 1 : 2

        return { ...task, ready, overdue, priority, time }
      })
      .sort((a, b) => a.priority - b.priority || (a.time || Number.MAX_SAFE_INTEGER) - (b.time || Number.MAX_SAFE_INTEGER))
  }, [data])

  if (!clickupConnected) return null

  const selectTask = (task) => {
    onSelectTask(task)
  }

  const startTask = (task) => {
    if (!task.ready) {
      selectTask(task)
      toast.info('Uzupełnij brakujące wymagane pola i uruchom tracking.')
      return
    }
    onStartTask(task)
  }

  return (
    <Paper elevation={1} css={styles.wrapper}>
      <div css={styles.header}>
        <div>
          <strong>Moje zadania z ClickUp</strong>
          <div css={styles.subtitle}>Dzisiaj i kontekst +/- 2 godziny</div>
        </div>
        <div css={styles.headerActions}>
          <IconButton
            size='small'
            aria-label='Odśwież zadania ClickUp'
            onClick={() => refetch()}
            disabled={loading}
          >
            <CachedIcon fontSize='small' />
          </IconButton>
          <IconButton
            size='small'
            aria-label={open ? 'Zwiń zadania ClickUp' : 'Rozwiń zadania ClickUp'}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>
      </div>
      <Collapse in={open}>
        <Divider />
        <div css={styles.list}>
          {loading && <div css={styles.message}>Pobieram zadania…</div>}
          {!loading && error && (
            <div css={styles.message}>
              Nie udało się pobrać zadań. Odśwież listę lub użyj ręcznego trackingu.
            </div>
          )}
          {!loading && !error && tasks.length === 0 && (
            <div css={styles.message}>Brak zadań na dzisiaj.</div>
          )}
          {!loading && !error && tasks.map((task) => (
            <div key={task._id} css={styles.task}>
              <div css={styles.taskMain} onClick={() => selectTask(task)} role='button' tabIndex={0}>
                <div css={styles.taskTitle}>{task.name}</div>
                <div css={styles.taskMeta}>
                  {task.time ? moment(task.time).format('DD.MM HH:mm') : 'bez godziny'}
                  {task.status?.status ? ` · ${task.status.status}` : ''}
                  {task.overdue && <Chip size='small' label='opóźnione' css={styles.overdue} />}
                </div>
              </div>
              <div css={styles.taskActions}>
                <IconButton
                  size='small'
                  aria-label='Otwórz zadanie w ClickUp'
                  component='a'
                  href={taskUrl(task._id)}
                  target='_blank'
                  rel='noreferrer noopener'
                >
                  <LaunchIcon fontSize='small' />
                </IconButton>
                <Button
                  size='small'
                  variant='contained'
                  color='primary'
                  startIcon={<PlayArrowIcon />}
                  disabled={isLoading}
                  onClick={() => startTask(task)}
                >
                  START
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Collapse>
    </Paper>
  )
}

const styles = {
  wrapper: { margin: '0.75rem', overflow: 'hidden' },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.5rem 0.75rem',
    fontSize: '0.85rem',
  },
  headerActions: { display: 'flex', alignItems: 'center' },
  subtitle: { color: colors.gray600, fontSize: '0.7rem', marginTop: 2 },
  list: { maxHeight: 260, overflowY: 'auto' },
  message: { padding: '1rem', color: colors.gray600, fontSize: '0.8rem' },
  task: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    padding: '0.55rem 0.75rem',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
  },
  taskMain: { minWidth: 0, flex: 1, cursor: 'pointer' },
  taskTitle: { fontSize: '0.8rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  taskMeta: { color: colors.gray600, fontSize: '0.68rem', display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 },
  overdue: { height: 18, fontSize: '0.6rem', backgroundColor: colors.red, color: colors.white },
  taskActions: { display: 'flex', alignItems: 'center', flexShrink: 0 },
}

export default ClickupTasksWidget
